import { Column } from '@access-erp/common';
import {
  CellClickedEvent,
  CellType,
  FilterValueTypes,
  StringFilterOption,
  ValueFormatterParams,
} from '@access-erp/ui-components';

import { renderUploadedBy } from '@/components/common/EnquiriesScreen/customFilters/UploadedByMultiSelect';
import {
  createLinkCellRenderer,
  shouldSkipCellClickNavigation,
} from '@/components/common/EnquiriesScreen/components/LinkCellRenderer';
import { CellFormatters } from '@/components/common/EnquiriesScreen/utils/CellFormatters';
import { filtersforStringifiedNumbers } from '@/components/common/EnquiriesScreen/utils/filtersForStringigfiedNumbers';
import { filterOrigin, filterUpliftsMethods, filterUpliftsStatuses }
  from '@/components/common/EnquiriesScreen/utils/statuses.config';
import { PaginatedFilteredUpliftType } from '@/graphql/contracts/_generated_/contracts';
import { guidFilterOptions } from '@/pages/SalesOrders/columns/filters.config';

const VISIBLE_REASON_CHARS_LENGTH = 50;

export interface UseTransactionTableColumnsReturn {
  headerColumns: Column<unknown>[];
}

type DefaultColumnArgsType = {
  getContractLink: (contractId: string, searchString?: string) => string;
  goToContract: (contractLink: string) => void;
};

const nonInteractableSettings = {
  disableRowGroup: true,
  sortable: false,
  filterable: false,
  suppressAdvancedFilter: true,
};

const nonSortableNotGroupableSettings = {
  disableRowGroup: true,
  sortable: false,
};

export const getHeaderColumns = ({
  getContractLink,
  goToContract,
}: DefaultColumnArgsType): Column<unknown>[] => [
  {
    name: 'contractNumber',
    caption: 'Contract Number',
    width: 200,
    cellType: CellType.String,
    defaultFilter: 1,
    cellRenderer: createLinkCellRenderer<PaginatedFilteredUpliftType>(({ data, searchString }) => {
      const contractId = data?.contractID ?? '';
      if (!contractId) {
        return '#';
      }
      return getContractLink(contractId, searchString);

    }),
    onCellClicked: (e: CellClickedEvent) => {
      if (shouldSkipCellClickNavigation(e)) {
        return;
      }
      const searchStringIfAny = e.context?.serverSide?.current?.search;
      const contractId = e.data.contractID ?? '';
      goToContract(getContractLink(contractId, searchStringIfAny));
    },
  },
  {
    name: 'effectiveDate',
    caption: 'Effective Date',
    width: 150,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    defaultFilter: 2,
  },
  {
    name: 'nextUpliftDate',
    caption: 'Next Uplift Date',
    width: 150,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    defaultFilter: 8,
  },
  {
    name: 'stockName',
    caption: 'Stock Name',
    width: 250,
    cellType: CellType.String,
    defaultFilter: 3,
  },
  {
    name: 'stockCode',
    caption: 'Stock Code',
    width: 200,
    cellType: CellType.String,
    defaultFilter: 4,
  },
  {
    name: 'upliftValue',
    caption: 'Uplift Value',
    width: 150,
    cellType: CellType.String,
    cellFormatter: CellFormatters.decimal,
    defaultFilter: 5,
    filterType: FilterValueTypes.StringFilter,
    filterOptions: {
      availableOptions: filtersforStringifiedNumbers,
    },
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'status',
    caption: 'Status',
    width: 100,
    cellType: CellType.String,
    filterType: FilterValueTypes.EnumFilter,
    filterData: filterUpliftsStatuses,
    defaultFilter: 6,
  },
  {
    name: 'upliftMethod',
    caption: 'Uplift Method',
    width: 150,
    cellType: CellType.String,
    filterType: FilterValueTypes.EnumFilter,
    filterData: filterUpliftsMethods,
    defaultFilter: 7,
    cellFormatter: ({ data }: ValueFormatterParams<unknown>) => {
      const upliftData = data as PaginatedFilteredUpliftType | undefined;
      if (upliftData?.upliftMethod === 'UnitCost') {
        const methodObj = upliftData && filterUpliftsMethods.find((method) => method.value === upliftData.upliftMethod);
        return methodObj ? methodObj.label : upliftData?.upliftMethod ?? '';
      }
      return upliftData?.upliftMethod ?? '';
    },
  },
  {
    name: 'createdBy',
    caption: 'Created By',
    width: 300,
    cellType: CellType.String,
    sortable: false,
    filterType: FilterValueTypes.StringMultiSelectFilter,
    filterOptions: {
      availableOptions: [StringFilterOption.ContainsValues],
    },
    renderFilter: (renderProps) => renderUploadedBy({ ...renderProps, title: 'Created by' }),
    defaultFilter: 9,
    disableRowGroup: true,
  },
  {
    name: 'origin',
    caption: 'Origin',
    width: 100,
    cellType: CellType.String,
    filterType: FilterValueTypes.EnumFilter,
    filterData: filterOrigin,
    defaultFilter: 10,
  },
  {
    name: 'created',
    caption: 'Created Time',
    width: 150,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    defaultFilter: 11,
  },
  {
    name: 'statusMessage',
    caption: 'Status Message',
    width: 370,
    cellType: CellType.String,
    defaultFilter: 12,
    cellRenderer: ({ value }) => {
      const reasonForFailureTrimmed =
          value && value.length > VISIBLE_REASON_CHARS_LENGTH
            ? value.slice(0, VISIBLE_REASON_CHARS_LENGTH) + '...'
            : value || '';
      const titlePopUp = value && value.length > VISIBLE_REASON_CHARS_LENGTH ? value : undefined;

      return <div title={titlePopUp}>{reasonForFailureTrimmed}</div>;
    },
  },
  {
    name: 'lastSalesOrderDate',
    caption: 'Last Invoice/SO Line Date',
    width: 200,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    ...nonInteractableSettings,
    defaultFilter: 13,
  },
  {
    name: 'lastSalesOrderValue',
    caption: 'Last Invoice/SO Line Value',
    width: 200,
    cellType: CellType.String,
    cellFormatter: CellFormatters.decimal,
    ...nonInteractableSettings,
    defaultFilter: 14,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'nextBillingPeriodValue',
    caption: 'Next Billing Period Value',
    width: 190,
    cellType: CellType.String,
    cellFormatter: CellFormatters.decimal,
    ...nonInteractableSettings,
    defaultFilter: 15,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'nextAnnualBillingPeriodValue',
    caption: 'Next Annual Billing Value',
    width: 190,
    cellType: CellType.String,
    cellFormatter: CellFormatters.decimal,
    ...nonInteractableSettings,
    defaultFilter: 16,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'customerAccountNumber',
    caption: 'Account',
    width: 140,
    cellType: CellType.String,
    defaultFilter: 17,
  },
  {
    name: 'billingPeriodValueDifference',
    caption: 'Total period value change',
    width: 190,
    cellType: CellType.String,
    cellFormatter: CellFormatters.decimal,
    ...nonInteractableSettings,
    defaultFilter: 19,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'annualBillingPeriodValueDifference',
    caption: 'Annual value change',
    width: 190,
    cellType: CellType.String,
    cellFormatter: CellFormatters.decimal,
    ...nonInteractableSettings,
    defaultFilter: 20,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'updated',
    caption: 'Edited (Updated) on',
    width: 160,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    defaultFilter: 21,
    ...nonSortableNotGroupableSettings,
  },
  {
    name: 'updatedBy',
    caption: 'Edited (Updated) by',
    width: 160,
    cellType: CellType.String,
    filterType: FilterValueTypes.StringMultiSelectFilter,
    filterOptions: {
      availableOptions: [StringFilterOption.ContainsValues],
    },
    renderFilter: (renderProps) =>
      renderUploadedBy({ ...renderProps, title: 'Edited (Updated) by' }),
    defaultFilter: 22,
    ...nonSortableNotGroupableSettings,
  },
  {
    name: 'iD',
    caption: 'ID',
    width: 270,
    cellType: CellType.String,
    defaultFilter: 23,
    hidden: true,
    filterType: FilterValueTypes.StringFilter,
    filterOptions: {
      availableOptions: guidFilterOptions,
    },
  },
];
